import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import PageHeader from '../components/PageHeader';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as SidebarNotifIcon } from '../assets/icons/sidebar_notification.svg';
import { format } from 'date-fns';
import { announcementApi } from '../services/api';
import { useAuth } from '../auth/AuthContext';

interface Announcement {
  id: string;
  title: string;
  content: string;
  category_name?: string;
  category_color?: string;
  is_pinned: boolean;
  is_read: boolean;
  created_at: string;
  image_url?: string;
}

const NotificationsPage: React.FC = () => {
  const { storeId } = useAuth();
  const navigate = useNavigate();

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (!storeId) return;

      try {
        setLoading(true);
        const data = await announcementApi.getStoreAnnouncements(storeId);
        setAnnouncements(data);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch announcements:', err);
        setError('お知らせの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [storeId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8f8f9' }}>
      <PageHeader
        title="お知らせ"
        icon={<SvgIcon component={SidebarNotifIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />

      {/* コンテンツ */}
      <Box sx={{ pb: 4, mx: { xs: -2, sm: 0 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {announcements.length === 0 ? (
          <Alert severity="info">お知らせはありません</Alert>
        ) : (
          <Box sx={{ bgcolor: '#f8f8f9' }}>
            <Box sx={{ height: '2px', bgcolor: '#e7e7e7', mx: { xs: -2, sm: -3 }, width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' } }} />
            {announcements.map((announcement, index) => (
              <React.Fragment key={announcement.id}>
                <Box
                  sx={{
                    pl: 5,
                    pr: 2,
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f0f0f0' },
                  }}
                  onClick={() => navigate(`/announcements/${announcement.id}`)}
                >
                  {/* タイトル行 */}
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 'bold',
                      color: '#3c3c3c',
                    }}
                  >
                    {announcement.title}
                  </Typography>

                  {/* 日付 */}
                  <Typography sx={{ fontSize: '12px', color: '#898989', mt: 0.5 }}>
                    {format(new Date(announcement.created_at), 'yyyy.MM.dd')}
                    {!announcement.is_read && (
                      <Box component="span" sx={{ ml: 1, color: '#0071e3', fontWeight: 'bold' }}>
                        未読
                      </Box>
                    )}
                  </Typography>
                </Box>
                <Box sx={{ height: '2px', bgcolor: '#e7e7e7', mx: { xs: -2, sm: -3 }, width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' } }} />
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NotificationsPage;
